import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Select, DatePicker, Space, Typography, Row, Col, Statistic, Spin, Button, Modal, Form, Input, Tabs, Popconfirm, message, Progress, Divider, Empty, Badge, Tooltip, Alert } from 'antd';
import { FileTextOutlined, UserOutlined, YoutubeOutlined, PlusOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined, MailOutlined, TeamOutlined, CheckCircleOutlined, CloseCircleOutlined, BarChartOutlined, CalendarOutlined, DownloadOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { useDeepLinkWhen } from '../utils/deeplink';

const { Title, Text } = Typography;

const WORK_TYPES = ['Video Upload', 'Thumbnail Design', 'SEO Optimization', 'Description Writing', 'Tags Update', 'Shorts Upload', 'Community Post', 'Video Editing', 'Audio Upload', 'Content Research', 'Scheduling', 'Analytics Review', 'Comment Management', 'Other'];
const workTypeColors = { 'Video Upload': 'red', 'Thumbnail Design': 'purple', 'SEO Optimization': 'blue', 'Description Writing': 'cyan', 'Tags Update': 'geekblue', 'Shorts Upload': 'magenta', 'Community Post': 'orange', 'Video Editing': 'volcano', 'Audio Upload': 'green', 'Content Research': 'gold', 'Scheduling': 'lime', 'Analytics Review': 'processing', 'Comment Management': 'default', 'Other': 'default' };

export default function WorkReport({ user }) {
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || user?.role === 'hr_manager';
  const [activeTab, setActiveTab] = useState(isAdmin ? 'admin' : 'my-work');
  // AI answers open this page on the asked day (?date=).
  const when = useDeepLinkWhen();

  // Staff: My Work
  const [myWork, setMyWork] = useState([]);
  const [myDate, setMyDate] = useState(when.date || dayjs());
  const [myLoading, setMyLoading] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [entries, setEntries] = useState([{ channel_id: undefined, work_type: 'Video Upload', video_title: '', youtube_link: '', notes: '' }]);
  const [channels, setChannels] = useState([]);
  const [videos, setVideos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Admin: View
  const [adminData, setAdminData] = useState({ logs: [], staff_summary: [], channels: [], staff: [] });
  const [adminDate, setAdminDate] = useState(when.date || dayjs());
  const [adminLoading, setAdminLoading] = useState(false);
  const [staffFilter, setStaffFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Summary
  const [summaryData, setSummaryData] = useState({ staff: [], channels: [], daily: [] });
  const [summaryPeriod, setSummaryPeriod] = useState('week');
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Assignments
  const [assignData, setAssignData] = useState({ work_assignments: [], channel_assignments: [] });
  const [assignModal, setAssignModal] = useState(false);
  const [channelModal, setChannelModal] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [manualChannels, setManualChannels] = useState([]);

  // Fetch channels for dropdown
  const fetchChannels = async () => {
    try {
      const res = await api.get('/work-report/channels');
      const yt = (res.data.youtube || []).map(c => ({ value: c.id, label: c.channel_name, source: 'youtube' }));
      const manual = (res.data.manual || []).map(c => ({ value: `m_${c.id}`, label: `${c.channel_name} (manual)`, source: 'manual', manualId: c.id }));
      setChannels([...yt, ...manual]);
      setManualChannels(res.data.manual || []);
    } catch (err) { console.error(err); }
  };

  // Video Tracker list for the "which video" picker
  const fetchVideos = async () => {
    try {
      const res = await api.get('/work-report/videos');
      setVideos(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); }
  };

  // Fetch my work
  const fetchMyWork = useCallback(async () => {
    setMyLoading(true);
    try {
      const res = await api.get('/work-report/my-work', { params: { date: myDate.format('YYYY-MM-DD') } });
      setMyWork(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); }
    setMyLoading(false);
  }, [myDate]);

  // Fetch admin view
  const fetchAdminView = useCallback(async () => {
    if (!isAdmin) return;
    setAdminLoading(true);
    try {
      const params = { date: adminDate.format('YYYY-MM-DD') };
      if (staffFilter) params.staff_id = staffFilter;
      if (channelFilter) params.channel_id = channelFilter;
      if (searchText) params.search = searchText;
      const res = await api.get('/work-report/admin-view', { params });
      setAdminData(res.data);
    } catch (err) { console.error(err); }
    setAdminLoading(false);
  }, [isAdmin, adminDate, staffFilter, channelFilter, searchText]);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    if (!isAdmin) return;
    setSummaryLoading(true);
    try {
      const res = await api.get('/work-report/summary', { params: { period: summaryPeriod } });
      setSummaryData(res.data);
    } catch (err) { console.error(err); }
    setSummaryLoading(false);
  }, [isAdmin, summaryPeriod]);

  // Fetch assignments
  const fetchAssignments = async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/work-report/assignments');
      setAssignData(res.data);
    } catch (err) { console.error(err); }
  };

  // Fetch staff list
  const fetchStaffList = async () => {
    try {
      const res = await api.get('/staff/list');
      setStaffList(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchChannels(); fetchVideos(); fetchMyWork(); if (isAdmin) { fetchStaffList(); } }, []);
  useEffect(() => { fetchMyWork(); }, [fetchMyWork]);
  useEffect(() => { if (activeTab === 'admin') fetchAdminView(); }, [fetchAdminView, activeTab]);
  useEffect(() => { if (activeTab === 'summary') fetchSummary(); }, [fetchSummary, activeTab]);
  useEffect(() => { if (activeTab === 'assignments') fetchAssignments(); }, [activeTab]);

  // Submit work entries
  const handleSubmit = async () => {
    const valid = entries.filter(e => e.channel_id && e.work_type);
    if (!valid.length) return message.warning('Add at least one work entry with channel selected');
    setSubmitting(true);
    try {
      const payload = valid.map(e => {
        const isManual = String(e.channel_id).startsWith('m_');
        return {
          channel_id: isManual ? null : e.channel_id,
          manual_channel_id: isManual ? parseInt(String(e.channel_id).replace('m_', '')) : null,
          custom_channel_name: isManual ? channels.find(c => c.value === e.channel_id)?.label?.replace(' (manual)', '') : null,
          work_type: e.work_type,
          video_id: e.video_id || null,
          upload_month: e.upload_month || null,
          video_title: e.video_title || null,
          youtube_link: e.youtube_link || null,
          notes: e.notes || null
        };
      });
      await api.post('/work-report/submit', { entries: payload, work_date: myDate.format('YYYY-MM-DD') });
      message.success(`${valid.length} work entries submitted!`);
      setAddModal(false);
      setEntries([{ channel_id: undefined, work_type: 'Video Upload', video_title: '', youtube_link: '', notes: '' }]);
      fetchMyWork();
      fetchVideos();
    } catch (err) { message.error('Failed to submit'); }
    setSubmitting(false);
  };

  // Delete entry
  const handleDelete = async (id) => {
    try {
      await api.delete(`/work-report/entry/${id}`);
      message.success('Deleted');
      fetchMyWork();
      if (isAdmin) fetchAdminView();
    } catch (err) { message.error('Failed'); }
  };

  // Search channels
  const handleSearch = async () => {
    if (searchQuery.length < 2) return message.warning('Enter at least 2 characters');
    setSearchLoading(true);
    try {
      const res = await api.get('/work-report/search', { params: { q: searchQuery } });
      setSearchResults(Array.isArray(res.data) ? res.data : []);
    } catch (err) { message.error('Search failed'); }
    setSearchLoading(false);
  };

  // Send pending emails
  const handleSendPending = async () => {
    try {
      const res = await api.post('/work-report/send-pending', { date: adminDate.format('YYYY-MM-DD') });
      message.success(`Pending work emails sent to ${res.data.emails_sent} staff members`);
    } catch (err) { message.error('Failed to send emails'); }
  };

  // Add manual channel
  const handleAddChannel = async (values) => {
    try {
      await api.post('/work-report/manual-channel', values);
      message.success('Channel added!');
      setChannelModal(false);
      fetchChannels();
    } catch (err) { message.error('Failed'); }
  };

  // Assign channel to staff
  const handleAssign = async (values) => {
    try {
      const isManual = String(values.channel_id).startsWith('m_');
      const payload = {
        user_id: values.user_id,
        channel_id: isManual ? null : values.channel_id,
        manual_channel_id: isManual ? parseInt(String(values.channel_id).replace('m_', '')) : null
      };
      await api.post('/work-report/assign', payload);
      message.success('Channel assigned!');
      setAssignModal(false);
      fetchAssignments();
    } catch (err) { message.error('Failed'); }
  };

  // Delete assignment
  const handleDeleteAssign = async (id) => {
    try {
      await api.delete(`/work-report/assign/${id}`);
      message.success('Removed');
      fetchAssignments();
    } catch (err) { message.error('Failed'); }
  };

  // Add entry row
  const addEntry = () => setEntries([...entries, { channel_id: undefined, work_type: 'Video Upload', video_title: '', youtube_link: '', notes: '' }]);
  const removeEntry = (i) => setEntries(entries.filter((_, idx) => idx !== i));
  const updateEntry = (i, key, val) => { const n = [...entries]; n[i][key] = val; setEntries(n); };

  // Export CSV
  const exportCSV = () => {
    const rows = [['Staff', 'Channel', 'Work Type', 'Title', 'YouTube Link', 'Notes', 'Date']];
    (adminData.logs || []).forEach(l => {
      rows.push([l.full_name || l.username, l.channel_name || l.custom_channel_name || '-', l.work_type, l.video_title || '-', l.youtube_link || '-', l.notes || '-', l.work_date]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `work-report-${adminDate.format('YYYY-MM-DD')}.csv`; a.click();
  };

  // =================== TAB: MY WORK ===================
  const myWorkTab = (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <DatePicker value={myDate} onChange={d => d && setMyDate(d)} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModal(true)}>Report Work</Button>
          <Button icon={<ReloadOutlined />} onClick={fetchMyWork}>Refresh</Button>
        </Space>
      </Card>
      {myLoading ? <Spin /> : myWork.length === 0 ? (
        <Empty description={<span>No work reported for {myDate.format('DD MMM YYYY')}. <Button type="link" onClick={() => setAddModal(true)}>Report now</Button></span>} />
      ) : (
        <Table size="small" dataSource={myWork} rowKey="id" pagination={false} scroll={{ x: 700 }}
          columns={[
            { title: 'Channel', key: 'ch', render: (_, r) => <span><YoutubeOutlined style={{ color: 'red', marginRight: 4 }} />{r.channel_name || r.custom_channel_name || '-'}</span> },
            { title: 'Work Type', dataIndex: 'work_type', render: v => <Tag color={workTypeColors[v] || 'default'}>{v}</Tag> },
            { title: 'Title', dataIndex: 'video_title', ellipsis: true, render: v => v || '-' },
            { title: 'YouTube Link', dataIndex: 'youtube_link', width: 80, render: v => v ? <a href={v} target="_blank" rel="noopener noreferrer">Open</a> : '-' },
            { title: 'Notes', dataIndex: 'notes', ellipsis: true, render: v => v || '-' },
            { title: '', width: 40, render: (_, r) => <Popconfirm title="Delete?" onConfirm={() => handleDelete(r.id)}><DeleteOutlined style={{ color: 'red', cursor: 'pointer' }} /></Popconfirm> }
          ]}
        />
      )}
    </>
  );

  // =================== TAB: ADMIN VIEW ===================
  const adminTab = (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <DatePicker value={adminDate} onChange={d => d && setAdminDate(d)} />
          <Select placeholder="All Staff" allowClear style={{ width: 160 }} showSearch optionFilterProp="label"
            value={staffFilter || undefined} onChange={v => setStaffFilter(v || '')}
            options={(adminData.staff || []).map(s => ({ value: s.id, label: s.full_name || s.username }))} />
          <Select placeholder="All Channels" allowClear style={{ width: 180 }} showSearch optionFilterProp="label"
            value={channelFilter || undefined} onChange={v => setChannelFilter(v || '')}
            options={(adminData.channels || []).map(c => ({ value: c.id, label: c.channel_name }))} />
          <Input.Search placeholder="Search..." style={{ width: 200 }} value={searchText}
            onChange={e => setSearchText(e.target.value)} onSearch={() => fetchAdminView()} allowClear />
          <Button icon={<ReloadOutlined />} onClick={fetchAdminView}>Refresh</Button>
          <Button icon={<DownloadOutlined />} onClick={exportCSV}>Export CSV</Button>
          <Tooltip title="Send pending work reminder to all staff"><Button type="primary" danger icon={<MailOutlined />} onClick={handleSendPending}>Send Pending Emails</Button></Tooltip>
        </Space>
      </Card>

      {adminLoading ? <Spin /> : (
        <>
          {/* Staff Summary Cards */}
          <div style={{ marginBottom: 16 }}>
            <Title level={5} style={{ marginBottom: 8 }}>Staff Summary — {adminDate.format('DD MMM YYYY')}</Title>
            <Row gutter={[8, 8]}>
              {(adminData.staff_summary || []).map(s => {
                const pct = s.total_assigned > 0 ? Math.round((s.total_worked / s.total_assigned) * 100) : 0;
                const color = s.total_pending > 0 ? '#fff2f0' : '#f6ffed';
                return (
                  <Col xs={24} sm={12} md={8} lg={6} key={s.staff_id}>
                    <Card size="small" style={{ background: color, borderColor: s.total_pending > 0 ? '#ffccc7' : '#b7eb8f' }}>
                      <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>
                        <UserOutlined /> {s.staff_name}
                      </div>
                      {s.total_assigned > 0 ? (
                        <>
                          <Progress percent={pct} size="small" status={s.total_pending > 0 ? 'exception' : 'success'} />
                          <div style={{ fontSize: 12, marginTop: 4 }}>
                            <Tag color="green">{s.total_worked} done</Tag>
                            <Tag color="red">{s.total_pending} pending</Tag>
                            <Tag color="blue">{s.total_assigned} assigned</Tag>
                          </div>
                          {s.total_pending > 0 && s.pending_channels.length > 0 && (
                            <div style={{ fontSize: 11, color: '#ff4d4f', marginTop: 4 }}>
                              <CloseCircleOutlined /> Not worked: {s.pending_channels.map(p => p.channel_name).join(', ')}
                            </div>
                          )}
                        </>
                      ) : (
                        <Text type="secondary" style={{ fontSize: 12 }}>No channels assigned</Text>
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>

          {/* Work Log Table */}
          <Card title={`Work Log — ${(adminData.logs || []).length} entries`} size="small">
            <Table size="small" dataSource={adminData.logs || []} rowKey="id" pagination={{ pageSize: 50, showTotal: t => `${t} entries` }}
              scroll={{ x: 900 }}
              columns={[
                { title: 'Staff', key: 'staff', width: 130, fixed: 'left', render: (_, r) => <span><UserOutlined style={{ marginRight: 4 }} />{r.full_name || r.username}</span> },
                { title: 'Channel', key: 'ch', width: 160, render: (_, r) => <span><YoutubeOutlined style={{ color: 'red', marginRight: 4 }} />{r.channel_name || r.custom_channel_name || '-'}</span> },
                { title: 'Work', dataIndex: 'work_type', width: 130, render: v => <Tag color={workTypeColors[v] || 'default'}>{v}</Tag> },
                { title: 'Title', dataIndex: 'video_title', ellipsis: true, render: v => v || '-' },
                { title: 'Link', dataIndex: 'youtube_link', width: 60, render: v => v ? <a href={v} target="_blank" rel="noopener noreferrer">Open</a> : '-' },
                { title: 'Notes', dataIndex: 'notes', ellipsis: true, width: 150, render: v => v || '-' },
                { title: '', width: 40, render: (_, r) => <Popconfirm title="Delete?" onConfirm={() => handleDelete(r.id)}><DeleteOutlined style={{ color: 'red', cursor: 'pointer' }} /></Popconfirm> }
              ]}
            />
          </Card>
        </>
      )}
    </>
  );

  // =================== TAB: SEARCH ===================
  const searchTab = (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space>
          <Input.Search placeholder="Search channel name..." style={{ width: 350 }} value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)} onSearch={handleSearch} enterButton={<><SearchOutlined /> Search</>} allowClear />
        </Space>
      </Card>
      {searchLoading ? <Spin /> : searchResults.length === 0 ? (
        <Empty description="Search a channel name to see assigned staff and recent work" />
      ) : (
        searchResults.map((r, i) => (
          <Card key={i} size="small" style={{ marginBottom: 12 }}
            title={<span><YoutubeOutlined style={{ color: 'red', marginRight: 6 }} />{r.channel.channel_name} {r.channel.channel_url && <a href={r.channel.channel_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>(Open)</a>}</span>}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Assigned Staff: </Text>
              {r.assigned_staff.length > 0 ? r.assigned_staff.map(s => <Tag key={s.id} color="blue"><UserOutlined /> {s.full_name || s.username}</Tag>) : <Tag color="orange">No one assigned</Tag>}
            </div>
            {r.recent_work.length > 0 ? (
              <Table size="small" dataSource={r.recent_work} rowKey="id" pagination={false}
                columns={[
                  { title: 'Date', dataIndex: 'work_date', width: 100, render: v => dayjs(v).format('DD MMM') },
                  { title: 'Staff', dataIndex: 'full_name', width: 120 },
                  { title: 'Work', dataIndex: 'work_type', render: v => <Tag color={workTypeColors[v] || 'default'}>{v}</Tag> },
                  { title: 'Title', dataIndex: 'video_title', ellipsis: true, render: v => v || '-' },
                  { title: 'Link', dataIndex: 'youtube_link', width: 60, render: v => v ? <a href={v} target="_blank" rel="noopener noreferrer">Open</a> : '-' }
                ]}
              />
            ) : <Text type="secondary">No work in last 7 days</Text>}
          </Card>
        ))
      )}
    </>
  );

  // =================== TAB: SUMMARY ===================
  const summaryTab = (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space>
          <Select value={summaryPeriod} onChange={setSummaryPeriod} style={{ width: 160 }}
            options={[{ value: 'week', label: 'Last 7 Days' }, { value: 'month', label: 'This Month' }]} />
          <Button icon={<ReloadOutlined />} onClick={fetchSummary}>Refresh</Button>
        </Space>
      </Card>
      {summaryLoading ? <Spin /> : (
        <>
          {/* Stats */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={6}><Card><Statistic title="Total Entries" value={(summaryData.staff || []).reduce((s, x) => s + parseInt(x.total_entries || 0), 0)} valueStyle={{ color: '#1890ff' }} /></Card></Col>
            <Col xs={6}><Card><Statistic title="Active Staff" value={(summaryData.staff || []).filter(x => parseInt(x.total_entries) > 0).length} valueStyle={{ color: '#52c41a' }} /></Card></Col>
            <Col xs={6}><Card><Statistic title="Active Days" value={(summaryData.daily || []).length} valueStyle={{ color: '#722ed1' }} /></Card></Col>
            <Col xs={6}><Card><Statistic title="Channels Touched" value={(summaryData.channels || []).filter(x => parseInt(x.total_entries) > 0).length} valueStyle={{ color: 'red' }} /></Card></Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Card title="Staff Productivity" size="small">
                <Table size="small" dataSource={(summaryData.staff || []).filter(x => parseInt(x.total_entries) > 0 || x.assigned_channels > 0)} rowKey="id" pagination={false}
                  columns={[
                    { title: 'Staff', key: 'name', render: (_, r) => r.full_name || r.username },
                    { title: 'Entries', dataIndex: 'total_entries', sorter: (a, b) => a.total_entries - b.total_entries, render: v => <Tag color="blue">{v}</Tag> },
                    { title: 'Days', dataIndex: 'days_worked', render: v => v || 0 },
                    { title: 'Channels', dataIndex: 'channels_worked', render: v => v || 0 },
                    { title: 'Assigned', dataIndex: 'assigned_channels', render: v => v || 0 },
                    { title: 'Score', dataIndex: 'productivity', render: v => <Progress percent={v || 0} size="small" status={v >= 80 ? 'success' : v >= 50 ? 'normal' : 'exception'} /> }
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Channel Activity" size="small">
                <Table size="small" dataSource={(summaryData.channels || []).filter(x => parseInt(x.total_entries) > 0)} rowKey="id" pagination={false}
                  columns={[
                    { title: 'Channel', dataIndex: 'channel_name', ellipsis: true },
                    { title: 'Entries', dataIndex: 'total_entries', sorter: (a, b) => a.total_entries - b.total_entries, render: v => <Tag color="green">{v}</Tag> },
                    { title: 'Staff', dataIndex: 'staff_count' },
                    { title: 'Days', dataIndex: 'active_days' },
                    { title: 'Workers', dataIndex: 'workers', ellipsis: true }
                  ]}
                />
              </Card>
            </Col>
          </Row>

          {/* Daily breakdown */}
          <Card title="Daily Breakdown" size="small" style={{ marginTop: 16 }}>
            <Table size="small" dataSource={summaryData.daily || []} rowKey="work_date" pagination={false}
              columns={[
                { title: 'Date', dataIndex: 'work_date', render: v => dayjs(v).format('DD MMM YYYY (ddd)') },
                { title: 'Entries', dataIndex: 'entries', render: v => <Tag color="blue">{v}</Tag> },
                { title: 'Staff', dataIndex: 'staff' },
                { title: 'Channels', dataIndex: 'channels' }
              ]}
            />
          </Card>
        </>
      )}
    </>
  );

  // =================== TAB: ASSIGNMENTS ===================
  const assignTab = (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAssignModal(true)}>Assign Channel to Staff</Button>
        <Button icon={<PlusOutlined />} onClick={() => setChannelModal(true)}>Add Manual Channel</Button>
        <Button icon={<ReloadOutlined />} onClick={fetchAssignments}>Refresh</Button>
      </Space>

      <Title level={5}>Work Report Assignments</Title>
      {(assignData.work_assignments || []).length === 0 ? <Empty description="No assignments yet — assign channels to staff above" /> : (
        (() => {
          const groups = {};
          (assignData.work_assignments || []).forEach(a => {
            const ch = a.channel_name || a.manual_name || 'Unknown';
            if (!groups[ch]) groups[ch] = [];
            groups[ch].push(a);
          });
          return Object.entries(groups).map(([ch, staff]) => (
            <Card key={ch} size="small" style={{ marginBottom: 8 }}
              title={<span><YoutubeOutlined style={{ color: 'red', marginRight: 6 }} />{ch}</span>}>
              <Space wrap>
                {staff.map(s => (
                  <Tag key={s.id} color="blue" closable onClose={() => handleDeleteAssign(s.id)}>
                    <UserOutlined /> {s.full_name || s.username}
                  </Tag>
                ))}
              </Space>
            </Card>
          ));
        })()
      )}

      <Divider />
      <Title level={5}>Existing Channel Assignments (from Staff Assignment page)</Title>
      {(assignData.channel_assignments || []).length === 0 ? <Text type="secondary">No existing assignments</Text> : (
        (() => {
          const groups = {};
          (assignData.channel_assignments || []).forEach(a => {
            if (!groups[a.channel_name]) groups[a.channel_name] = [];
            groups[a.channel_name].push(a);
          });
          return Object.entries(groups).map(([ch, staff]) => (
            <Card key={ch} size="small" style={{ marginBottom: 8 }}
              title={<span><YoutubeOutlined style={{ color: 'red', marginRight: 6 }} />{ch}</span>}>
              <Space wrap>
                {staff.map(s => (
                  <Tag key={s.id} color="purple"><UserOutlined /> {s.full_name || s.username}</Tag>
                ))}
              </Space>
            </Card>
          ));
        })()
      )}
    </>
  );

  // Build tabs
  const tabItems = [];
  tabItems.push({ key: 'my-work', label: <span><FileTextOutlined /> My Work</span>, children: myWorkTab });
  if (isAdmin) {
    tabItems.push({ key: 'admin', label: <span><TeamOutlined /> Daily Report</span>, children: adminTab });
    tabItems.push({ key: 'search', label: <span><SearchOutlined /> Search Channel</span>, children: searchTab });
    tabItems.push({ key: 'summary', label: <span><BarChartOutlined /> Summary</span>, children: summaryTab });
    tabItems.push({ key: 'assignments', label: <span><TeamOutlined /> Channel Assignments</span>, children: assignTab });
  }

  return (
    <div>
      <Title level={3}><FileTextOutlined /> Daily Work Report</Title>
      <Text type="secondary">Track daily work — which staff did what on which channel</Text>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} style={{ marginTop: 16 }} />

      {/* Submit Work Modal */}
      <Modal title="Report Today's Work" open={addModal} onCancel={() => setAddModal(false)} width={700} footer={[
        <Button key="add" onClick={addEntry} icon={<PlusOutlined />}>Add Row</Button>,
        <Button key="submit" type="primary" loading={submitting} onClick={handleSubmit} icon={<CheckCircleOutlined />}>Submit All</Button>
      ]}>
        <Alert message={`Reporting work for: ${myDate.format('DD MMM YYYY')}`} type="info" showIcon style={{ marginBottom: 16 }} />
        {entries.map((e, i) => (
          <Card key={i} size="small" style={{ marginBottom: 8, background: '#fafafa' }}
            extra={entries.length > 1 && <DeleteOutlined style={{ color: 'red', cursor: 'pointer' }} onClick={() => removeEntry(i)} />}>
            <Row gutter={8}>
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: 12 }}>Channel *</Text>
                  <Select placeholder="Select channel" style={{ width: '100%' }} showSearch optionFilterProp="label"
                    value={e.channel_id} onChange={v => updateEntry(i, 'channel_id', v)}
                    options={channels} />
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: 12 }}>Work Type *</Text>
                  <Select style={{ width: '100%' }} value={e.work_type} onChange={v => updateEntry(i, 'work_type', v)}
                    options={WORK_TYPES.map(w => ({ value: w, label: w }))} />
                </div>
              </Col>
              <Col xs={24} sm={16}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: 12 }}>Video (Video Tracker list se select karo)</Text>
                  <Select allowClear showSearch optionFilterProp="label" style={{ width: '100%' }}
                    placeholder="Kaun video upload kiya"
                    value={e.video_id}
                    onChange={v => {
                      updateEntry(i, 'video_id', v);
                      const vid = videos.find(x => x.id === v);
                      if (vid && !e.video_title) updateEntry(i, 'video_title', vid.song_title);
                    }}
                    options={videos.map(v => ({
                      value: v.id,
                      label: `${v.song_title}${v.artist ? ' — ' + v.artist : ''}${v.status === 'uploaded' ? ' (already uploaded)' : ''}`,
                    }))} />
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: 12 }}>Upload Month</Text>
                  <Input type="month" value={e.upload_month || myDate.format('YYYY-MM')}
                    onChange={ev => updateEntry(i, 'upload_month', ev.target.value)} />
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: 12 }}>Video Title</Text>
                  <Input placeholder="Title..." value={e.video_title} onChange={ev => updateEntry(i, 'video_title', ev.target.value)} />
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: 12 }}>YouTube Link</Text>
                  <Input placeholder="https://youtube.com/..." value={e.youtube_link} onChange={ev => updateEntry(i, 'youtube_link', ev.target.value)} />
                </div>
              </Col>
              <Col xs={24}>
                <div>
                  <Text strong style={{ fontSize: 12 }}>Notes</Text>
                  <Input placeholder="Any notes..." value={e.notes} onChange={ev => updateEntry(i, 'notes', ev.target.value)} />
                </div>
              </Col>
            </Row>
          </Card>
        ))}
      </Modal>

      {/* Assign Channel Modal */}
      <Modal title="Assign Channel to Staff" open={assignModal} onCancel={() => setAssignModal(false)} footer={null}>
        <Form onFinish={handleAssign} layout="vertical">
          <Form.Item name="channel_id" label="Channel" rules={[{ required: true }]}>
            <Select placeholder="Select channel" showSearch optionFilterProp="label" options={channels} />
          </Form.Item>
          <Form.Item name="user_id" label="Staff" rules={[{ required: true }]}>
            <Select placeholder="Select staff" showSearch optionFilterProp="label"
              options={staffList.map(s => ({ value: s.id, label: s.full_name || s.username }))} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>Assign</Button>
        </Form>
      </Modal>

      {/* Add Manual Channel Modal */}
      <Modal title="Add Manual Channel" open={channelModal} onCancel={() => setChannelModal(false)} footer={null}>
        <Form onFinish={handleAddChannel} layout="vertical">
          <Form.Item name="channel_name" label="Channel Name" rules={[{ required: true }]}>
            <Input placeholder="Channel name" />
          </Form.Item>
          <Form.Item name="channel_link" label="Channel Link">
            <Input placeholder="https://youtube.com/@..." />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>Add Channel</Button>
        </Form>
      </Modal>
    </div>
  );
}
